import * as Immutable from "immutable";
import SharedEntity from "logic/permissions/SharedEntity";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import Role from "logic/roles/Role";
import UserOverview from "logic/users/UserOverview";
import PaginationURL from "util/PaginationURL";
import Team from "../logic/Team";
const apiPrefix = "/plugins/org.graylog.plugins.security";
const ApiRoutes = {
  TeamsApiController: {
    loadTeamsForUser: (userId) => ({ url: `${apiPrefix}/teams/member/${userId}` }),
    loadTeamsForRole: (roleId) => ({ url: `${apiPrefix}/authz/roles/${roleId}/teams` }),
    loadUsersForTeam: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}/members` }),
    loadRolesForTeam: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}/roles` }),
    loadPaginated: () => ({ url: `${apiPrefix}/teams` }),
    addMembers: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}/members` }),
    addMemberToTeams: (userId) => ({ url: `${apiPrefix}/teams/${userId}/teams` }),
    addRoleToTeams: (roleId) => ({ url: `${apiPrefix}/authz/roles/${roleId}/teams/add` }),
    removeMember: (teamId, userId) => ({ url: `${apiPrefix}/teams/${teamId}/members/${userId}` }),
    removeRole: (roleId) => ({ url: `${apiPrefix}/authz/roles/${roleId}/teams/remove` }),
    create: () => ({ url: `${apiPrefix}/teams` }),
    delete: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}` }),
    load: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}` }),
    update: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}` }),
    teamSharesPaginated: (teamId) => ({ url: `${apiPrefix}/teams/shares/${teamId}` })
  }
};
export const TEAMS_QUERY_KEY = ["teams"];
const formatPaginatedSharesResponse = ({
  additional_queries: additionalQueries,
  context,
  count,
  entities,
  page,
  per_page: perPage,
  query,
  total
}) => ({
  list: Immutable.List(entities.map((se) => SharedEntity.fromJSON(se))),
  context: {
    granteeCapabilities: context.grantee_capabilities
  },
  pagination: {
    additionalQueries,
    page,
    perPage,
    query,
    count,
    total
  }
});
const _responseToPaginatedList = ({ count, total, page, per_page, query, teams, context }) => ({
  context: {
    users: Immutable.Map(context == null ? void 0 : context.users),
    roles: Immutable.Map(context == null ? void 0 : context.roles)
  },
  list: Immutable.List(teams.map(Team.fromJSON)),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  }
});
const _responseToPaginatedUserList = ({
  count,
  total,
  page,
  per_page,
  query,
  users,
  context
}) => ({
  list: Immutable.List(users.map((u) => UserOverview.fromJSON(u))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  },
  adminUser: (context == null ? void 0 : context.admin_user) ? UserOverview.fromJSON(context == null ? void 0 : context.admin_user) : void 0
});
const _responseToPaginatedRolesList = ({
  count,
  total,
  page,
  per_page,
  query,
  roles,
  context
}) => ({
  list: Immutable.List(roles.map((r) => Role.fromJSON(r))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  },
  context
});
export const createTeam = (team, share_request) => {
  const { url } = ApiRoutes.TeamsApiController.create();
  return fetch("POST", qualifyUrl(url), { entity: team.toJSON(), share_request }).then(Team.fromJSON);
};
export const loadTeam = (teamId) => {
  const { url } = ApiRoutes.TeamsApiController.load(teamId);
  return fetch("GET", qualifyUrl(url)).then(Team.fromJSON);
};
export const updateTeam = (team) => {
  const { url } = ApiRoutes.TeamsApiController.update(team.id);
  return fetch("PUT", qualifyUrl(url), team.toJSON()).then(Team.fromJSON);
};
export const deleteTeam = (team) => {
  const { url } = ApiRoutes.TeamsApiController.delete(team.id);
  return fetch("DELETE", qualifyUrl(url));
};
export const addMembers = (teamId, userIds) => {
  const { url } = ApiRoutes.TeamsApiController.addMembers(teamId);
  return fetch("PUT", qualifyUrl(url), userIds.toArray()).then(Team.fromJSON);
};
export const addMemberToTeams = (userId, teamIds) => {
  const { url } = ApiRoutes.TeamsApiController.addMemberToTeams(userId);
  return fetch("PUT", qualifyUrl(url), teamIds.toArray());
};
export const addRoleToTeams = (roleId, teamIds) => {
  const { url } = ApiRoutes.TeamsApiController.addRoleToTeams(roleId);
  return fetch("PUT", qualifyUrl(url), teamIds.toArray());
};
export const removeMember = (teamId, userId) => {
  const { url } = ApiRoutes.TeamsApiController.removeMember(teamId, userId);
  return fetch("DELETE", qualifyUrl(url)).then(Team.fromJSON);
};
export const removeRole = (roleId, teamId) => {
  const { url } = ApiRoutes.TeamsApiController.removeRole(roleId);
  return fetch("PUT", qualifyUrl(url), teamId);
};
export const loadRolesForTeam = (teamId, _teamName, { page, perPage, query }) => {
  const url = PaginationURL(ApiRoutes.TeamsApiController.loadRolesForTeam(teamId).url, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedRolesList);
};
export const loadUsersForTeam = (teamId, _teamName, { page, perPage, query }) => {
  const url = PaginationURL(ApiRoutes.TeamsApiController.loadUsersForTeam(teamId).url, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedUserList);
};
export const loadTeamsForUser = (userId, { page, perPage, query }) => {
  const url = PaginationURL(ApiRoutes.TeamsApiController.loadTeamsForUser(userId).url, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
};
export const loadTeamsForRole = (roleId, { page, perPage, query }) => {
  const url = PaginationURL(ApiRoutes.TeamsApiController.loadTeamsForRole(roleId).url, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
};
export const loadTeamsPaginated = ({ page, perPage, query }) => {
  const url = PaginationURL(ApiRoutes.TeamsApiController.loadPaginated().url, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
};
export const loadTeamSharesPaginated = (teamId, { page, perPage, query, additionalQueries }) => {
  const url = PaginationURL(
    ApiRoutes.TeamsApiController.teamSharesPaginated(teamId).url,
    page,
    perPage,
    query,
    additionalQueries
  );
  return fetch("GET", qualifyUrl(url)).then(formatPaginatedSharesResponse);
};
